import type { MonthlyStackEntry } from '@fleet/shared';
import { BUCKETS } from '@fleet/shared';
import { StackedBars, type Staaf } from '../../components/charts/StackedBars';
import { InfoTip } from '../../components/ui/InfoTip';
import { Panel } from '../../components/ui/Panel';

/**
 * The fleet's variable costs per month, split by category.
 *
 * The same quantity the variable tracker reports and the same one that forms the
 * non-fixed half of the TCO below: every cost line there is, fuel through
 * maintenance. It was called "uitgaven" for a while, which made it look like a
 * third kind of number rather than one of the two the TCO is made of.
 *
 * Drawn by the same `StackedBars` as that TCO panel, and that is the point: they
 * sit under one another, so a second hand-rolled chart meant two bar widths, two
 * legends and two ways of reading off an amount. This one used fixed 34px bars,
 * leaving half a wide panel empty while the chart below stretched across it.
 *
 * The legend is the filter, and the read-out line under the bars carries the
 * per-category totals — which is what the separate "kosten per categorie" card
 * used to do.
 */
export function MonthlyCostStack({ months }: { months: MonthlyStackEntry[] }) {
  const jaar = months[0]?.maand.slice(0, 4) ?? '';
  const staven: Staaf[] = months.map((m) => ({
    key: m.maand,
    label: m.label,
    waarden: Object.fromEntries(m.buckets.map((b) => [b.key, b.value])),
    gedempt: m.future,
  }));

  // Alleen de lagen die ergens in het jaar voorkomen, zodat de legenda geen
  // categorie toont die nergens op de as staat.
  const lagen = BUCKETS.filter((b) =>
    months.some((m) => (m.buckets.find((x) => x.key === b.key)?.value ?? 0) > 0),
  );

  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>
          Variabele kosten per maand
        </span>
        <InfoTip
          uitlijning="links"
          tekst="Alles wat via de tankpas en de onderhoudsfacturen is geboekt, over het hele wagenpark en excl. btw. Hele maanden; de laatste maand loopt meestal nog, want de MTC-factuur knipt er middenin af."
        />
      </div>
      <StackedBars lagen={lagen} staven={staven} hoogte={210} totaalLabel={`${jaar} · totaal`} />
    </Panel>
  );
}
